// ── One sentence in, a whole task out ────────────────────────────────
// Pure and dependency-free, so the drawer and the backfill script can share
// it. They MUST share it: a second copy of a keyword map drifts (the J3U set
// volume rules live in three places and already disagree).
//
// Two rules this file exists to keep:
//   1. Every claim reports character offsets, so the UI can light up the
//      coach's own words and let a click withdraw the claim. A parser that
//      cannot show its work will not be trusted the first time it is wrong.
//   2. `today` is passed in, never read from the clock, so tests are
//      timezone-proof.

export const TASK_TYPES = ['nutrition', 'workouts', 'checkin', 'message', 'review', 'admin'];

// Order is irrelevant to the result: within a category, findType scans every
// keyword and keeps the earliest text position, not the first one it meets
// in this list; across categories, `review` wins outright and otherwise the
// earliest position wins. See findType.
export const TYPE_KEYWORDS = [
  ['nutrition', ['nutrition', 'macros', 'calories', 'diet', 'meals', 'meal', 'carbs', 'protein', 'fats', 'fat', 'foods', 'food']],
  ['workouts', ['workouts', 'workout', 'program', 'training', 'blocks', 'block', 'lifts', 'lift', 'exercises', 'exercise', 'deload', 'queue', 'phase']],
  ['checkin', ['check-in', 'checkin', 'check in']],
  ['message', ['message', 'text', 'reply', 'dm', 'sms', 'email', 'send', 'share']],
  ['review', ['review', 'look at', 'assess']],
];

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MAX_MENTION = 40;

const nameOf = (c) => String((c && c.name) || '');

function iso(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
function addDays(todayIso, n) {
  const d = new Date(`${todayIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
}

// ── dates: a CLOSED grammar ──────────────────────────────────────────
// Enumerable, therefore testable, therefore incapable of inventing an
// interpretation. A general NL date library is what turns "Create monthly
// report" into a task that repeats forever.
function findDue(text, todayIso) {
  const dow = new Date(`${todayIso}T00:00:00Z`).getUTCDay();
  const patterns = [
    [/\b(today)\b/i, () => todayIso],
    [/\b(tomorrow)\b/i, () => addDays(todayIso, 1)],
    [/\b(next week)\b/i, () => addDays(todayIso, 7)],
    [/\bin (\d{1,2}) days?\b/i, (m) => addDays(todayIso, parseInt(m[1], 10))],
    // Weekday, optionally led by by/on/next. Always the NEXT occurrence:
    // "monday" said on a Monday means the one coming, not the one today.
    [new RegExp(`\\b(?:(?:by|on|next)\\s+)?(${DAYS.map((d) => `${d}|${d.slice(0, 3)}`).join('|')})\\b`, 'i'),
      (m) => {
        const want = DAYS.findIndex((d) => d.startsWith(m[1].toLowerCase().slice(0, 3)));
        return addDays(todayIso, ((want - dow + 7) % 7) || 7);
      }],
    // m/d, US order. d/m is deliberately unsupported: a format that silently
    // means two things is worse than one that does not parse.
    [/\b(\d{1,2})\/(\d{1,2})\b/, (m) => {
      const mo = parseInt(m[1], 10), da = parseInt(m[2], 10);
      if (mo < 1 || mo > 12 || da < 1 || da > 31) return null;
      return `${todayIso.slice(0, 4)}-${String(mo).padStart(2, '0')}-${String(da).padStart(2, '0')}`;
    }],
  ];
  for (const [re, resolve] of patterns) {
    const m = text.match(re);
    if (!m) continue;
    const value = resolve(m);
    if (!value) continue;
    // "migrate to platform by 8/10" — the preposition describes the DUE DATE,
    // so it belongs to the date's claim and must come out of the task's name.
    // Only the weekday pattern used to carry it (Clayton, 2026-08-05: a task
    // read "migrate to platform by"); doing it here covers every date form,
    // and any pattern added later gets it for free.
    //
    // The leading \b is load-bearing: without it "standby tomorrow" would lose
    // the "by" out of the middle of a word.
    let start = m.index;
    const lead = text.slice(0, start).match(/\b(?:due\s+by|due|by|on|before|until)\s+$/i);
    if (lead) start = lead.index;
    return { start, end: m.index + m[0].length, value, text: text.slice(start, m.index + m[0].length) };
  }
  return null;
}

// ── type: review wins outright, otherwise earliest wins ──────────────
function findType(text) {
  const hits = [];
  for (const [type, words] of TYPE_KEYWORDS) {
    let best = null;
    for (const w of words) {
      const m = text.match(new RegExp(`\\b${w.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i'));
      if (m && (!best || m.index < best.start)) {
        best = { type, start: m.index, end: m.index + m[0].length, text: m[0] };
      }
    }
    if (best) hits.push(best);
  }
  if (!hits.length) return null;
  const review = hits.find((h) => h.type === 'review');
  return review || hits.sort((a, b) => a.start - b.start)[0];
}

// ── client: only ever a FULL @name match ──────────────────────────────
// We have the client list, so match it directly instead of guessing where
// a name ends from what follows it (a date word, a dash, end-of-string —
// that guess breaks the moment the tag isn't the last thing in the
// sentence, which is the normal case: "call @Will Swanson about renewal").
// Longest name first, so "Mike Mouser" wins over any shorter name that is
// a prefix of it. No full name match under an "@" means no claim — a
// partial name is an in-progress mention for the suggestion list, not
// something the parser should guess at.
function findClient(text, clients) {
  const byLongestName = clients
    .map((c) => ({ client: c, name: nameOf(c) }))
    .filter(({ name }) => name)
    .sort((a, b) => b.name.length - a.name.length);

  const atRe = /(^|\s)@/g;
  let m;
  while ((m = atRe.exec(text))) {
    const start = m.index + m[1].length;
    const after = text.slice(start + 1);
    const afterLower = after.toLowerCase();
    for (const { client, name } of byLongestName) {
      if (!afterLower.startsWith(name.toLowerCase())) continue;
      const end = start + 1 + name.length;
      const nextChar = text[end];
      if (nextChar !== undefined && /[a-zA-Z0-9]/.test(nextChar)) continue; // don't split a longer word
      const nameLower = name.toLowerCase();
      const pool = clients.filter((c) => nameOf(c).toLowerCase() === nameLower);
      if (pool.length > 1) return null; // ambiguous — never pick a person on the coach's behalf
      return { start, end, id: client.id, text: text.slice(start, end) };
    }
  }
  return null;
}

export function parseTask(text, { clients = [], today } = {}) {
  const raw = String(text || '');
  const spans = [];

  // Notes split first — everything after a SPACED dash. "check-in" survives.
  const sep = raw.search(/\s+[-—]\s+/);
  const head = sep >= 0 ? raw.slice(0, sep) : raw;
  const notes = sep >= 0 ? raw.slice(sep).replace(/^\s+[-—]\s+/, '').trim() || null : null;

  const client = findClient(head, clients);
  if (client) spans.push({ kind: 'client', start: client.start, end: client.end, value: client.id });

  const due = findDue(head, today);
  if (due) spans.push({ kind: 'due', start: due.start, end: due.end, value: due.value });

  const type = findType(head);
  if (type) spans.push({ kind: 'type', start: type.start, end: type.end, value: type.type });

  // The title is what is left once the client and date are lifted out. The
  // TYPE keyword stays — it is usually the verb, and removing it would leave
  // "for Mickey Mouse".
  let title = head;
  [client, due].filter(Boolean)
    .sort((a, b) => b.start - a.start)
    .forEach((s) => { title = title.slice(0, s.start) + title.slice(s.end); });
  title = title.replace(/\s+(for|to|with)\s*$/i, '').replace(/\s{2,}/g, ' ').trim();

  return {
    title,
    notes,
    clientId: client ? client.id : null,
    dueDate: due ? due.value : null,
    category: type ? type.type : 'admin',
    spans: spans.sort((a, b) => a.start - b.start),
  };
}

// ── the suggestion list, as derived state ────────────────────────────
// Returns the in-progress mention under the caret, or null. THIS is the fix
// for the popup that four commits could not close: a token that already names
// a real client is RESOLVED, not in progress, so it returns null and the list
// is simply not rendered. There is no popup to close, therefore no call site
// that can forget to close it.
export function mentionAt(text, caret, clients = []) {
  const upto = String(text || '').slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;     // must start a word
  const query = upto.slice(at + 1);
  if (query.length > MAX_MENTION || query.includes('@')) return null;
  const queryLower = query.toLowerCase();
  // Resolved once the query STARTS WITH a full client name at a word
  // boundary — not only when it equals one. Without the boundary check, a
  // client whose name is a strict prefix of another (e.g. "Mick" vs "Mickey
  // Mouse") would close the list while the coach is still mid-word.
  const resolved = clients.some((c) => {
    const name = nameOf(c).toLowerCase();
    if (!name || !queryLower.startsWith(name)) return false;
    const nextChar = query[name.length];
    return nextChar === undefined || !/[a-zA-Z0-9]/.test(nextChar);
  });
  if (resolved) return null;
  return { query, start: at, end: caret };
}
